/**
 * Signals Menu
 *
 * One dropdown for the four chart signal overlays that each used to own a
 * button in the action bar — Fair Price, Diamonds, Displacement and Trade
 * Entries — plus the settings gears that used to sit beside them.
 *
 * Follows the LevelsToggle / SRToggle pattern: an `action-bar-btn` trigger and
 * a portalled, fixed-position dropdown that closes on outside click or Escape.
 *
 * Two deliberate behaviour notes:
 *  - Every gear is shown unconditionally. In the action bar a gear only
 *    appeared while its overlay was enabled; in a menu, being able to open the
 *    settings of something that is currently off is the point.
 *  - Toggling a row keeps the menu open (you often flip several); a gear opens
 *    a modal or panel over the chart, so it closes the menu.
 *
 * ## Where the Trades settings live, and why they are the exception
 *
 * Trades has exactly one setting. A modal for one checkbox is the wrong shape,
 * and the other two options are worse:
 *
 *  - A portalled popover anchored to the gear would sit OUTSIDE `expandedRef`,
 *    so the dropdown's own outside-click handler would read every click in it
 *    as a click away and shut the menu — including the click on the checkbox.
 *    Escaping that means threading a second ref into the handler, a second
 *    Escape listener, and a second lot of viewport-clamping maths, all to
 *    reproduce what the dropdown already does.
 *  - Adding it to the outside-click exception list keeps the popover open but
 *    leaves two independently positioned surfaces to keep from colliding.
 *
 * So it expands INLINE, as a section of the dropdown itself. Being a child of
 * `expandedRef` means the existing outside-click test already excludes it, the
 * existing Escape already closes it with its parent, and there is no second
 * position to compute. The cost is that this one gear does not close the menu —
 * which is the correct behaviour anyway for a setting you want to see take
 * effect while the menu is still up.
 *
 * Store subscriptions are primitives ONLY. A selector returning a fresh object
 * or array compares unequal under `Object.is` on every render and sends React
 * into an infinite loop (error #185) — that took the chart down once already.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Check, Settings, X } from 'lucide-react';
import { useFairPriceStore } from '../store/fairPriceStore';
import { useDisplacementStore } from '../store/displacementStore';
import {
  useTradeMarkerStore,
  useTradeMarkersEnabled,
  useTradeMarkerLinksEnabled,
} from '../store/tradeMarkerStore';
import { useCandidateStore, useCandidatesEnabled } from '../store/candidateStore';
import './SignalsMenu.css';

/** Roughly the dropdown width, used to keep it inside the viewport. */
const DROPDOWN_WIDTH = 260;

interface SignalRow {
  key: string;
  label: string;
  /** Tooltip on the toggle — carried over from the old action-bar buttons. */
  title: string;
  enabled: boolean;
  onToggle: () => void;
  /** Absent for rows with no settings of their own. */
  gear?: {
    title: string;
    onClick: () => void;
    /** Diamonds' gear reflects whether its side panel is currently open. */
    active?: boolean;
    /**
     * The gear expands a section of this dropdown rather than opening
     * something over the chart, so it must NOT close the menu. See the module
     * note on why the Trades settings are inline.
     */
    inline?: boolean;
  };
  /** Rendered under the row while its inline gear is open. */
  panel?: React.ReactNode;
}

interface SignalsMenuProps {
  /** Opens the Fair Price settings modal (state lives in ChartView). */
  onOpenFairPriceSettings: () => void;
  /** Opens the Displacement settings modal (state lives in ChartView). */
  onOpenDisplacementSettings: () => void;
}

const SignalsMenu: React.FC<SignalsMenuProps> = ({
  onOpenFairPriceSettings,
  onOpenDisplacementSettings,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  /** Key of the row whose inline settings section is open, if any. */
  const [openPanelKey, setOpenPanelKey] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  // --- Store state. Every one of these selects a primitive boolean. ---
  const fairPriceEnabled = useFairPriceStore((state) => state.enabled);
  const displacementEnabled = useDisplacementStore((state) => state.enabled);
  const tradeMarkersEnabled = useTradeMarkersEnabled();
  const tradeLinksEnabled = useTradeMarkerLinksEnabled();
  const candidatesEnabled = useCandidatesEnabled();
  const candidatePanelOpen = useCandidateStore((state) => state.panelOpen);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    // Collapse the inline settings with the menu. Leaving it open means the
    // dropdown reopens taller than it was closed, which reads as a glitch.
    setOpenPanelKey(null);
  }, []);

  /** An inline gear expands its own section and closes any other. */
  const toggleInlinePanel = useCallback((key: string) => {
    setOpenPanelKey((prev) => (prev === key ? null : key));
  }, []);

  // Position the dropdown under the trigger, clamped to the viewport.
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - DROPDOWN_WIDTH - 8)),
      });
    }
  }, [isExpanded]);

  // Close on click outside.
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        expandedRef.current && !expandedRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, handleClose]);

  // Close on Escape.
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, handleClose]);

  /** Gear actions open something over the chart, so they close the menu. */
  const runGearAction = useCallback((action: () => void) => {
    action();
    handleClose();
  }, [handleClose]);

  // Plain local array — not a zustand selector, so it cannot cause a loop.
  const rows: SignalRow[] = [
    {
      key: 'fairPrice',
      label: 'Fair Price',
      title: fairPriceEnabled
        ? 'Hide Fair Price indicator'
        : 'Show Fair Price indicator (anchors, phases, A+/A signals)',
      enabled: fairPriceEnabled,
      onToggle: () => useFairPriceStore.getState().setEnabled(!fairPriceEnabled),
      gear: {
        title: 'Fair Price settings',
        onClick: onOpenFairPriceSettings,
      },
    },
    {
      key: 'diamonds',
      label: 'Diamonds',
      title: candidatesEnabled
        ? 'Hide diamonds'
        : 'Diamonds — auto-graded setup candidates you can vote on',
      enabled: candidatesEnabled,
      onToggle: () => useCandidateStore.getState().setEnabled(!candidatesEnabled),
      gear: {
        title: candidatePanelOpen
          ? 'Hide diamonds panel'
          : 'Diamonds settings, auto-tag and score',
        onClick: () => useCandidateStore.getState().setPanelOpen(!candidatePanelOpen),
        active: candidatePanelOpen,
      },
    },
    {
      key: 'displacement',
      label: 'Displacement',
      title: displacementEnabled
        ? 'Hide displacement candles'
        : 'Show displacement candles (big clean expansion bars that close beyond the previous candle)',
      enabled: displacementEnabled,
      onToggle: () => useDisplacementStore.getState().setEnabled(!displacementEnabled),
      gear: {
        title: 'Displacement settings',
        onClick: onOpenDisplacementSettings,
      },
    },
    {
      key: 'tradeMarkers',
      // "Trades", not "Trade Entries" — it draws exits and the connectors now,
      // not just entries.
      label: 'Trades',
      title: tradeMarkersEnabled
        ? 'Hide trades'
        : 'Trades — where you and the bot actually got in, and where it ended',
      enabled: tradeMarkersEnabled,
      onToggle: () => useTradeMarkerStore.getState().setEnabled(!tradeMarkersEnabled),
      gear: {
        title: 'Trade marker settings',
        onClick: () => toggleInlinePanel('tradeMarkers'),
        active: openPanelKey === 'tradeMarkers',
        inline: true,
      },
      panel: (
        <>
          <label className="signals-menu-panel-row">
            <input
              type="checkbox"
              checked={tradeLinksEnabled}
              onChange={() =>
                useTradeMarkerStore.getState().setShowLinks(!tradeLinksEnabled)
              }
            />
            <span>Show entry-to-exit lines</span>
          </label>
          <p className="signals-menu-panel-hint">
            A faint dotted line from each entry to the fill that closed it.
          </p>
        </>
      ),
    },
  ];

  // Collapsing four buttons into a menu hid which overlays are on. The count
  // gives that back without opening it.
  const enabledCount = rows.filter((r) => r.enabled).length;

  return (
    <div ref={containerRef} className="signals-menu-container">
      <button
        className={`action-bar-btn ${enabledCount > 0 ? 'signals-menu-enabled' : ''}`}
        onClick={handleToggleExpand}
        title={
          enabledCount > 0
            ? `Signal overlays — ${enabledCount} of ${rows.length} on`
            : 'Signal overlays — none on'
        }
      >
        <Activity size={14} className="action-bar-icon" />
        <span className="action-bar-label">Signals</span>
        {/* Inline, in the button's flow — NOT absolutely positioned.
            `.action-bar-btn` is overflow:hidden, so a badge pinned to the corner
            gets clipped. This is the same shape S/R and Bots already use. */}
        {enabledCount > 0 && (
          <span className="signals-menu-count">{enabledCount}</span>
        )}
      </button>

      {isExpanded && createPortal(
        <div
          ref={expandedRef}
          className="signals-menu-dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 10000,
          }}
        >
          <div className="signals-menu-header">
            <span>Signals</span>
            <button className="signals-menu-close-btn" onClick={handleClose} title="Close">
              <X size={14} />
            </button>
          </div>

          <div className="signals-menu-list">
            {rows.map((row) => {
              // Pulled out so TypeScript narrows it inside the click closure.
              const gear = row.gear;
              return (
                <React.Fragment key={row.key}>
                  <div className="signals-menu-row">
                    <button
                      className={`signals-menu-item ${row.enabled ? 'active' : ''}`}
                      onClick={row.onToggle}
                      title={row.title}
                    >
                      <span className="signals-menu-item-label">{row.label}</span>
                      {row.enabled && <Check size={14} className="signals-menu-check" />}
                    </button>

                    {gear ? (
                      <button
                        className={`signals-menu-gear ${gear.active ? 'active' : ''}`}
                        // An inline gear expands a section of THIS dropdown, so
                        // closing the menu would immediately unmount what it
                        // just opened. See the module note.
                        onClick={() =>
                          gear.inline ? gear.onClick() : runGearAction(gear.onClick)
                        }
                        title={gear.title}
                      >
                        <Settings size={12} />
                      </button>
                    ) : (
                      <span className="signals-menu-gear-spacer" />
                    )}
                  </div>

                  {row.panel && openPanelKey === row.key && (
                    <div className="signals-menu-panel">{row.panel}</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SignalsMenu;
